import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';
import { formatApiError } from '../utils/errorHandler';
import { Button } from '../components/ui/button';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../components/ui/select';
import { 
  RefreshCw, CheckCircle2, XCircle, Clock, AlertCircle, Building2, Home, Users 
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DuesTrackingPage = () => {
  const { t, language } = useLanguage();
  const currency = language === 'tr' ? '₺' : '$';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchDues();
  }, []);

  const fetchDues = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/dues/summary`, { withCredentials: true });
      setData(response.data);
    } catch (error) {
      toast.error(t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API_URL}/api/dues/generate`, {}, { withCredentials: true });
      toast.success(response.data.message);
      fetchDues();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setGenerating(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid': return <CheckCircle2 className="w-4 h-4 text-emerald-600" />;
      case 'partial': return <Clock className="w-4 h-4 text-amber-500" />;
      case 'overdue': return <XCircle className="w-4 h-4 text-red-600" />;
      default: return <AlertCircle className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusLabel = (status) => {
    const map = {
      paid: language === 'tr' ? 'Ödendi' : 'Paid',
      partial: language === 'tr' ? 'Kısmi' : 'Partial',
      overdue: language === 'tr' ? 'Gecikmiş' : 'Overdue',
      unpaid: language === 'tr' ? 'Bekliyor' : 'Pending'
    };
    return map[status] || status;
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'paid': return 'bg-emerald-50 text-emerald-700 border-emerald-300';
      case 'partial': return 'bg-amber-50 text-amber-700 border-amber-300';
      case 'overdue': return 'bg-red-50 text-red-700 border-red-300';
      default: return 'bg-gray-50 text-gray-600 border-gray-300';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-black border-t-transparent animate-spin"></div>
      </div>
    );
  }

  const accruals = data?.accruals || [];
  const filtered = statusFilter === 'all' ? accruals : accruals.filter(a => a.status === statusFilter);

  return (
    <div className="space-y-6" data-testid="dues-tracking-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">{t('duesTracking')}</h1>
          <p className="text-gray-500 text-sm mt-1">{data?.total_count || 0} {language === 'tr' ? 'kayıt' : 'records'}</p>
        </div>
        <Button 
          onClick={handleGenerate}
          disabled={generating}
          className="bg-black text-white rounded-none hover:bg-gray-800"
          data-testid="generate-dues-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${generating ? 'animate-spin' : ''}`} />
          {t('generateDues')}
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white border border-gray-200 p-4">
          <p className="text-xs uppercase tracking-widest text-gray-500 mb-1">{t('totalDues')}</p>
          <p className="font-heading text-2xl font-bold" data-testid="total-dues">{currency}{(data?.total_amount || 0).toLocaleString()}</p>
        </div>
        <div className="bg-white border border-emerald-200 p-4">
          <p className="text-xs uppercase tracking-widest text-gray-500 mb-1">{t('paidDues')}</p>
          <p className="font-heading text-2xl font-bold text-emerald-600">{currency}{(data?.total_paid || 0).toLocaleString()}</p>
        </div>
        <div className="bg-white border border-red-200 p-4">
          <p className="text-xs uppercase tracking-widest text-gray-500 mb-1">{t('remainingDues')}</p>
          <p className="font-heading text-2xl font-bold text-red-600">{currency}{(data?.total_remaining || 0).toLocaleString()}</p>
        </div>
        <div className="bg-white border border-amber-200 p-4">
          <p className="text-xs uppercase tracking-widest text-gray-500 mb-1">{t('overdueCount')}</p>
          <p className="font-heading text-2xl font-bold text-amber-600">{data?.overdue_count || 0}</p>
        </div>
      </div>

      {/* Filter */}
      <div className="flex gap-4">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48 rounded-none" data-testid="dues-status-filter">
            <SelectValue placeholder={t('status')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('all')}</SelectItem>
            <SelectItem value="unpaid">{language === 'tr' ? 'Bekliyor' : 'Pending'}</SelectItem>
            <SelectItem value="overdue">{t('overdueCount')}</SelectItem>
            <SelectItem value="partial">{t('partialCount')}</SelectItem>
            <SelectItem value="paid">{language === 'tr' ? 'Ödendi' : 'Paid'}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Dues Table */}
      {filtered.length === 0 ? (
        <div className="bg-white border border-gray-200 p-12 text-center">
          <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">{t('noDues')}</p>
          <p className="text-sm text-gray-400 mt-1">
            {language === 'tr' ? 'Bina ayarlarından aylık aidat tutarı belirleyin ve "Aidat Oluştur" butonuna tıklayın' : 'Set monthly dues in building settings and click "Generate Dues"'}
          </p>
        </div>
      ) : (
        <div className="border border-gray-200 overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr className="bg-gray-50">
                <th>{t('description')}</th>
                <th>{language === 'tr' ? 'Bina' : 'Building'}</th>
                <th>{t('units')}</th>
                <th>{t('tenants')}</th>
                <th>{t('dueDate')}</th>
                <th>{t('amount')}</th>
                <th>{t('paidAmount')}</th>
                <th>{t('remainingAmount')}</th>
                <th>{t('status')}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((accrual) => (
                <tr key={accrual.id} data-testid={`dues-row-${accrual.id}`}>
                  <td className="font-medium text-sm">{accrual.description}</td>
                  <td>
                    <span className="flex items-center gap-1 text-sm">
                      <Building2 className="w-3.5 h-3.5 text-gray-400" />
                      {accrual.building_name}
                    </span>
                  </td>
                  <td>
                    <span className="flex items-center gap-1 text-sm">
                      <Home className="w-3.5 h-3.5 text-gray-400" />
                      {accrual.unit_name}
                    </span>
                  </td>
                  <td>
                    <span className="flex items-center gap-1 text-sm">
                      <Users className="w-3.5 h-3.5 text-gray-400" />
                      {accrual.tenant_name}
                    </span>
                  </td>
                  <td className="text-sm text-gray-600">{accrual.due_date?.split('T')[0]}</td>
                  <td className="font-mono">{currency}{accrual.amount.toLocaleString()}</td>
                  <td className="font-mono text-emerald-600">{currency}{accrual.paid_amount.toLocaleString()}</td>
                  <td className="font-mono font-bold text-red-600">
                    {accrual.remaining_amount > 0 ? `${currency}${accrual.remaining_amount.toLocaleString()}` : '-'}
                  </td>
                  <td>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-bold uppercase border ${getStatusBadgeClass(accrual.status)}`}>
                      {getStatusIcon(accrual.status)}
                      {getStatusLabel(accrual.status)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default DuesTrackingPage;
